import type { FC, ReactNode } from 'react';
import React, { memo } from 'react';
import { styled } from '@compiled/react';
import isEqual from 'lodash/isEqual';

import { token } from '@atlaskit/tokens';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';

import type { Space } from '@confluence/space-utils/entry-points/useSpacesData.ts';

import { SpacesDropdownItem } from './SpacesDropdownItem.tsx';

type SpacesDropdownSectionProps = {
	listId: string;
	isNav4Enabled: boolean;
	heading?: ReactNode;
	spaces?: Space[];
	currentSpace?: Space;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionContainer = styled.div({
	margin: `${token('space.200')} 0 ${token('space.100')} 0`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginBottom: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SectionHeading = styled.h2({
	color: token('color.text.subtle'),
	font: token('font.heading.xxsmall'),
	margin: `0 0 ${token('space.100')} ${token('space.200')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const SpaceList = styled.ul({
	listStyleType: 'none',
	width: '100%',
	padding: 0,
	margin: 0,
});

const SECTION_TITLE_ID_PREFIX = 'spaces-dropdown-section-header-';

export const SpacesDropdownSection: FC<SpacesDropdownSectionProps> = memo(
	({ listId, isNav4Enabled, heading, spaces, currentSpace }) => {
		const spaceList = (
			<SpaceList aria-labelledby={`${SECTION_TITLE_ID_PREFIX}${listId}`}>
				{currentSpace && (
					<SpacesDropdownItem
						space={currentSpace}
						key={currentSpace.alias || currentSpace.id!}
						listId={listId}
						isNav4Enabled={isNav4Enabled}
					/>
				)}
				{spaces &&
					spaces.map((space) => {
						return (
							<SpacesDropdownItem
								space={space}
								key={space.id}
								isNav4Enabled={isNav4Enabled}
								listId={listId}
							/>
						);
					})}
			</SpaceList>
		);

		if (isNav4Enabled) {
			return (
				<MenuSection>
					{heading && <MenuSectionHeading>{heading}</MenuSectionHeading>}
					{spaceList}
				</MenuSection>
			);
		}

		return (
			<SectionContainer>
				{heading && (
					<SectionHeading id={`${SECTION_TITLE_ID_PREFIX}${listId}`}>{heading}</SectionHeading>
				)}
				{spaceList}
			</SectionContainer>
		);
	},
	isEqual,
);
